import type { ControlInput, FlightState } from '../flight/FlightState';
import type { ControlLock } from '../flight/ControlLock';
import type { TargetInstance } from '../combat/TargetField';
import type { MissionRunner } from '../mission/MissionRunner';
import type { Terrain } from '../world/Terrain';
import type { Loop } from '../core/Loop';

/**
 * `window.vectorZero` — the simulation's state, readable from the page console (`?state=1`).
 *
 * Written for automated flying. Driving this game from outside meant scraping the numeric readout,
 * a DOM panel that refreshes at 5 Hz with pitch to one decimal and position to the metre, and that
 * resolution is the reason an autopilot built on it could put the aircraft within 20 m of the
 * relay mast and no closer — the mast's body radius is 2.5 m. Reading the state directly removes
 * the instrument from the loop.
 *
 * Three properties make it safe to leave in the build:
 *
 * - **It is inert unless asked for.** Nothing is installed without `?state=1`, and even then the
 *   cost is zero until `sample()` is called: there is no per-frame work and no subscription.
 * - **It cannot write.** Every field is a copy of a number. There is no handle on any system, so
 *   nothing here can perturb a run — which matters because the simulation is deterministic and a
 *   debug tool that could nudge it would make the checksum meaningless.
 * - **It does not allocate.** `sample()` fills one pooled object and returns it, so a controller
 *   polling at 50 Hz adds nothing to the heap. The consequence, stated plainly because it will
 *   surprise someone: the returned object is *reused*, so keeping a reference and expecting a
 *   history gives you the same values repeatedly. Copy what you need.
 *
 * Units throughout: metres, m/s, degrees, seconds. Degrees rather than the radians used
 * internally, because everything on this side of the boundary is read by a human or by a script
 * written by one, and that is where CLAUDE.md says the conversion belongs.
 */

export interface VectorZeroAircraft {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Heading, degrees clockwise from north, [0, 360). */
  headingDeg: number;
  pitchDeg: number;
  bankDeg: number;
  alphaDeg: number;
  betaDeg: number;
  rollRateDegS: number;
  pitchRateDegS: number;
  yawRateDegS: number;
  airspeedMs: number;
  groundSpeedMs: number;
  verticalSpeedMs: number;
  /** Above mean sea level. */
  altitudeM: number;
  /** Above the terrain beneath, which is what the HUD shows. */
  altitudeAglM: number;
  loadFactor: number;
  integrity: number;
  fuelKg: number;
  throttleCommand: number;
  throttleActual: number;
  engineRunning: boolean;
  stalled: boolean;
  stallDepth: number;
  structuralFailure: boolean;
  crashed: boolean;
}

/**
 * The filtered stick, after ramping and device mixing — the command the flight model consumed.
 *
 * The single most useful field here for anything driving the keyboard. A controller that models
 * the key ramp itself drifts from the real axis over a long flight, and the divergence shows up as
 * an aircraft that pitches up while the controller believes it is pushing down.
 */
export interface VectorZeroControls {
  roll: number;
  pitch: number;
  yaw: number;
  throttle: number;
  /** Seconds left on the mission-start control hold; 0 once the stick is live. */
  lockRemainingS: number;
}

export interface VectorZeroTarget {
  designation: string;
  label: string;
  /** Aim point — what a strike has to reach, not the base of the structure. */
  aimX: number;
  aimY: number;
  aimZ: number;
  /** Within this of the aim point counts as striking it at all, metres. */
  bodyRadiusM: number;
  destroyed: boolean;
  integrity: number;
}

export interface VectorZeroCheckpoint {
  id: string;
  x: number;
  y: number;
  z: number;
  /** Capture radius, metres. The objective tests 3-D distance against it. */
  radiusM: number;
}

export interface VectorZeroObjective {
  id: string;
  kind: string;
  description: string;
  state: string;
  primary: boolean;
  progress: number;
}

export interface VectorZeroState {
  simTimeS: number;
  tick: number;
  paused: boolean;
  /** True while a menu owns the screen and the simulation is not advancing. */
  menuUp: boolean;
  missionId: string;
  missionPhase: string;
  aircraft: VectorZeroAircraft;
  controls: VectorZeroControls;
  /** The next uncompleted `reach` objective, or null when none is outstanding. */
  checkpoint: VectorZeroCheckpoint | null;
  targets: VectorZeroTarget[];
  objectives: VectorZeroObjective[];
}

export interface VectorZeroBridge {
  /** Bumped when a field changes meaning, so a script can refuse to run against a newer game. */
  readonly version: number;
  /** Current state. Fills and returns a pooled object — copy anything you intend to keep. */
  sample(): VectorZeroState;
}

/** What the bridge needs to read. Everything is borrowed; nothing is retained or written. */
export interface StateBridgeSources {
  readonly loop: Loop;
  readonly terrain: Terrain;
  readonly flightState: () => FlightState;
  readonly crashed: () => boolean;
  readonly command: () => ControlInput;
  readonly controlLock: ControlLock;
  readonly targets: () => readonly TargetInstance[];
  readonly runner: () => MissionRunner;
  readonly menuUp: () => boolean;
}

const RAD_TO_DEG = 180 / Math.PI;
const BRIDGE_VERSION = 1;

/**
 * Where the bridge is published. `window` in the game; a plain object in a test.
 *
 * Injected rather than reaching for the global, matching `StorageLike` in `Save.ts` — it keeps the
 * mapping testable headlessly, which is the half of this file that can actually be wrong.
 */
export interface BridgeHost {
  vectorZero?: VectorZeroBridge;
}

/** Install the bridge on `host`, and return it. */
export function installStateBridge(
  sources: StateBridgeSources,
  host: BridgeHost = window as unknown as BridgeHost,
): VectorZeroBridge {
  const state = blankState();

  const bridge: VectorZeroBridge = {
    version: BRIDGE_VERSION,
    sample(): VectorZeroState {
      fill(state, sources);
      return state;
    },
  };

  host.vectorZero = bridge;
  return bridge;
}

function fill(state: VectorZeroState, sources: StateBridgeSources): void {
  const flight = sources.flightState();
  const runner = sources.runner();

  state.simTimeS = sources.loop.time.simTimeS;
  state.tick = sources.loop.time.tick;
  state.paused = sources.loop.isPaused;
  state.menuUp = sources.menuUp();
  state.missionId = runner.mission.id;
  state.missionPhase = runner.phase;

  const a = state.aircraft;
  a.x = flight.position.x;
  a.y = flight.position.y;
  a.z = flight.position.z;
  a.vx = flight.velocity.x;
  a.vy = flight.velocity.y;
  a.vz = flight.velocity.z;
  a.headingDeg = flight.headingRad * RAD_TO_DEG;
  a.pitchDeg = flight.pitchRad * RAD_TO_DEG;
  a.bankDeg = flight.bankRad * RAD_TO_DEG;
  a.alphaDeg = flight.alphaRad * RAD_TO_DEG;
  a.betaDeg = flight.betaRad * RAD_TO_DEG;
  a.rollRateDegS = flight.rollRateRadS * RAD_TO_DEG;
  a.pitchRateDegS = flight.pitchRateRadS * RAD_TO_DEG;
  a.yawRateDegS = flight.yawRateRadS * RAD_TO_DEG;
  a.airspeedMs = flight.airspeedMs;
  a.groundSpeedMs = flight.groundSpeedMs;
  a.verticalSpeedMs = flight.verticalSpeedMs;
  a.altitudeM = flight.altitudeM;
  a.altitudeAglM = sources.terrain.altitudeAglM(flight.position);
  a.loadFactor = flight.loadFactor;
  a.integrity = flight.integrity;
  a.fuelKg = flight.fuelKg;
  a.throttleCommand = flight.throttleCommand;
  a.throttleActual = flight.throttleActual;
  a.engineRunning = flight.engineRunning;
  a.stalled = flight.stalled;
  a.stallDepth = flight.stallDepth;
  a.structuralFailure = flight.structuralFailure;
  a.crashed = sources.crashed();

  const command = sources.command();
  state.controls.roll = command.rollCmd;
  state.controls.pitch = command.pitchCmd;
  state.controls.yaw = command.yawCmd;
  state.controls.throttle = command.throttle;
  state.controls.lockRemainingS = sources.controlLock.remainingS;

  const checkpoint = runner.nextCheckpoint();
  if (checkpoint === null) {
    state.checkpoint = null;
  } else {
    const into = checkpointScratch;
    into.id = checkpoint.id;
    into.x = checkpoint.positionM.x;
    into.y = checkpoint.positionM.y;
    into.z = checkpoint.positionM.z;
    into.radiusM = checkpoint.radiusM;
    state.checkpoint = into;
  }

  // Both lists are rewritten in place and only grow, so a steady mission allocates nothing.
  const targets = sources.targets();
  state.targets.length = 0;
  for (let i = 0; i < targets.length; i += 1) {
    const target = targets[i];
    const row = targetAt(i);
    row.designation = target.designation;
    row.label = target.label;
    row.aimX = target.aimPointM.x;
    row.aimY = target.aimPointM.y;
    row.aimZ = target.aimPointM.z;
    row.bodyRadiusM = target.params.bodyRadiusM;
    row.destroyed = target.destroyed;
    row.integrity = target.integrity;
    state.targets.push(row);
  }

  const statuses = runner.objectives.statuses;
  state.objectives.length = 0;
  for (let i = 0; i < statuses.length; i += 1) {
    const status = statuses[i];
    const row = objectiveAt(i);
    row.id = status.id;
    row.kind = status.kind;
    row.description = status.description;
    row.state = status.state;
    row.primary = status.primary;
    row.progress = status.progress;
    state.objectives.push(row);
  }
}

const checkpointScratch: VectorZeroCheckpoint = { id: '', x: 0, y: 0, z: 0, radiusM: 0 };
const targetPool: VectorZeroTarget[] = [];
const objectivePool: VectorZeroObjective[] = [];

function targetAt(index: number): VectorZeroTarget {
  let row = targetPool[index];
  if (row === undefined) {
    row = {
      designation: '', label: '', aimX: 0, aimY: 0, aimZ: 0,
      bodyRadiusM: 0, destroyed: false, integrity: 1,
    };
    targetPool[index] = row;
  }
  return row;
}

function objectiveAt(index: number): VectorZeroObjective {
  let row = objectivePool[index];
  if (row === undefined) {
    row = { id: '', kind: '', description: '', state: '', primary: false, progress: 0 };
    objectivePool[index] = row;
  }
  return row;
}

function blankState(): VectorZeroState {
  return {
    simTimeS: 0,
    tick: 0,
    paused: false,
    menuUp: false,
    missionId: '',
    missionPhase: '',
    aircraft: {
      x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0,
      headingDeg: 0, pitchDeg: 0, bankDeg: 0, alphaDeg: 0, betaDeg: 0,
      rollRateDegS: 0, pitchRateDegS: 0, yawRateDegS: 0,
      airspeedMs: 0, groundSpeedMs: 0, verticalSpeedMs: 0,
      altitudeM: 0, altitudeAglM: 0, loadFactor: 1, integrity: 1, fuelKg: 0,
      throttleCommand: 0, throttleActual: 0,
      engineRunning: false, stalled: false, stallDepth: 0,
      structuralFailure: false, crashed: false,
    },
    controls: { roll: 0, pitch: 0, yaw: 0, throttle: 0, lockRemainingS: 0 },
    checkpoint: null,
    targets: [],
    objectives: [],
  };
}
